import { useEffect, useMemo, useState, Fragment } from "react";
import { listAudit, undoImport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rank } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  History, Filter, ChevronDown, ChevronRight, Undo2, ShieldCheck, RefreshCw,
  Package, User, DollarSign, ListChecks, Settings as SettingsIcon, Building2, UserCog, Upload,
} from "lucide-react";

const ENTITY_META = {
  sale:               { label: "Sale",              icon: DollarSign,   tone: "text-emerald-300" },
  lead:               { label: "Lead",              icon: ListChecks,   tone: "text-blue-300" },
  customer_meta:      { label: "Customer meta",     icon: User,         tone: "text-cyan-300" },
  payment:            { label: "Payment",           icon: DollarSign,   tone: "text-amber-300" },
  slabs:              { label: "Slabs",             icon: SettingsIcon, tone: "text-fuchsia-300" },
  programme_catalog:  { label: "Programmes",        icon: Package,      tone: "text-pink-300" },
  sale_types:         { label: "Sale types",        icon: ListChecks,   tone: "text-orange-300" },
  objections:         { label: "Objections",        icon: ListChecks,   tone: "text-orange-300" },
  payment_fees:       { label: "Payment fees",      icon: DollarSign,   tone: "text-yellow-300" },
  audit_exercises:    { label: "Audit exercises",   icon: ListChecks,   tone: "text-teal-300" },
  facility:           { label: "Facility",          icon: Building2,    tone: "text-indigo-300" },
  user_role:          { label: "Role change",       icon: UserCog,      tone: "text-rose-300" },
  import:             { label: "Import",            icon: Upload,       tone: "text-lime-300" },
};

const ACTION_TONE = {
  create:      "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  update:      "bg-blue-500/10 text-blue-300 border-blue-500/30",
  delete:      "bg-rose-500/10 text-rose-300 border-rose-500/30",
  role_change: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  import:      "bg-lime-500/10 text-lime-300 border-lime-500/30",
  import_undo: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
};

export default function AuditLog() {
  const { user } = useAuth();
  const isMaster = rank(user?.role) >= rank("master_admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState({
    entity_type: "__all__",
    action: "__all__",
    actor_email: "",
    from_date: "",
    to_date: "",
    scope: "facility",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => {
        if (v && v !== "__all__") params[k] = v;
      });
      const data = await listAudit(params);
      setRows(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const set = (k, v) => setFilter((f) => ({ ...f, [k]: v }));

  const doUndo = async (row) => {
    if (!window.confirm(`Undo this import — "${row.filename}"?\n\nThis will DELETE ${row.created_ids?.length || 0} row(s) that this import created. Rows that were only updated will be left alone.`)) return;
    try {
      const res = await undoImport(row.id);
      toast.success(`Undo complete · ${res.deleted} row(s) removed`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    }
  };

  const grouped = useMemo(() => {
    const g = { today: [], yesterday: [], older: [] };
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYest = startOfToday - 86400000;
    rows.forEach((r) => {
      const t = new Date(r.at).getTime();
      if (t >= startOfToday) g.today.push(r);
      else if (t >= startOfYest) g.yesterday.push(r);
      else g.older.push(r);
    });
    return g;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <History className="h-3.5 w-3.5 text-amber-400" />
            Change history
          </div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Audit Log</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Every edit to a published record — who changed what, when, and the before/after values. Scoped to the active facility by default.
          </p>
        </div>
        <Button onClick={refresh} variant="outline" className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414]" data-testid="audit-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <section className="card-surface p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          <Filter className="h-3 w-3" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Record type</Label>
            <Select data-testid="audit-log-select-128" value={filter.entity_type} onValueChange={(v) => set("entity_type", v)}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="audit-filter-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-h-72">
                <SelectItem data-testid="audit-log-select-item-133" value="__all__" className="focus:bg-[#1a1a1a]">All types</SelectItem>
                {Object.entries(ENTITY_META).map(([k, m]) => (
                  <SelectItem data-testid="audit-log-select-item-135" key={k} value={k} className="focus:bg-[#1a1a1a]">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Action</Label>
            <Select data-testid="audit-log-select-142" value={filter.action} onValueChange={(v) => set("action", v)}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="audit-filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                <SelectItem data-testid="audit-log-select-item-147" value="__all__" className="focus:bg-[#1a1a1a]">All actions</SelectItem>
                {["create", "update", "delete", "role_change", "import", "import_undo"].map((a) => (
                  <SelectItem data-testid="audit-log-select-item-149" key={a} value={a} className="focus:bg-[#1a1a1a]">{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Actor email</Label>
            <Input
              value={filter.actor_email}
              onChange={(e) => set("actor_email", e.target.value)}
              placeholder="user@company.com"
              className="bg-[#0a0a0a] border-[#262626] mt-1.5"
              data-testid="audit-filter-email"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">From</Label>
            <Input type="date" value={filter.from_date} onChange={(e) => set("from_date", e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="audit-filter-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">To</Label>
            <Input type="date" value={filter.to_date} onChange={(e) => set("to_date", e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="audit-filter-to" />
          </div>
          <div className="flex items-end gap-2">
            {isMaster && (
              <button
                type="button"
                onClick={() => set("scope", filter.scope === "all" ? "facility" : "all")}
                className={`text-[11px] rounded-full px-3 py-1.5 border transition-colors ${
                  filter.scope === "all"
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                    : "border-[#3a3a3a] bg-[#0a0a0a] text-zinc-300 hover:border-amber-500/40"
                }`}
                data-testid="audit-scope-toggle"
                title="Show every facility's audit log (master_admin only)"
              >
                <ShieldCheck className="inline h-3 w-3 mr-1" /> {filter.scope === "all" ? "All facilities" : "Active facility"}
              </button>
            )}
            <Button onClick={refresh} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="audit-apply-filters">
              Apply
            </Button>
          </div>
        </div>
      </section>

      {["today", "yesterday", "older"].map((bucket) => (
        grouped[bucket].length > 0 && (
          <section key={bucket}>
            <div className="eyebrow mb-3">
              {bucket === "today" ? "Today" : bucket === "yesterday" ? "Yesterday" : "Older"}
              <span className="text-zinc-500 ml-2 normal-case tracking-normal text-[11px]">({grouped[bucket].length})</span>
            </div>
            <div className="card-surface overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                    <th className="px-4 py-3 font-medium w-8"></th>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Who</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Record</th>
                    <th className="px-4 py-3 font-medium">Detail</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped[bucket] || []).map((r) => {
                    const meta = ENTITY_META[r.entity_type] || { label: r.entity_type, icon: ListChecks, tone: "text-zinc-300" };
                    const Icon = meta.icon;
                    const open = expanded === r.id;
                    return (
                      <Fragment key={r.id}>
                        <tr className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors" data-testid={`audit-row-${r.id}`}>
                          <td className="px-4 py-3.5">
                            <button
                              onClick={() => setExpanded(open ? null : r.id)}
                              className="text-zinc-500 hover:text-zinc-200"
                              data-testid={`audit-expand-${r.id}`}
                            >
                              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-zinc-400 font-mono whitespace-nowrap">
                            {formatTime(r.at)}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="text-sm font-medium">{r.actor_name || "—"}</div>
                            <div className="text-[10px] text-zinc-500">{r.actor_email || ""}</div>
                          </td>
                          <td className="px-4 py-3.5">
                            <Badge className={`${ACTION_TONE[r.action] || ACTION_TONE.update} border`}>
                              {r.action}
                            </Badge>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className={`inline-flex items-center gap-1.5 ${meta.tone}`}>
                              <Icon className="h-3.5 w-3.5" />
                              <span className="text-[11px] uppercase tracking-[0.15em] font-semibold">{meta.label}</span>
                            </div>
                            {r.entity_label && <div className="text-xs text-zinc-300 mt-0.5">{r.entity_label}</div>}
                          </td>
                          <td className="px-4 py-3.5 text-xs text-zinc-400">
                            {r.entity_type === "import" ? (
                              <div>
                                <span className="text-zinc-300">{r.filename}</span>
                                {r.import_counts && (
                                  <span className="ml-2 text-[10px]">
                                    (+{r.import_counts.created || 0} · ~{r.import_counts.updated || 0} · !{r.import_counts.errors || 0})
                                  </span>
                                )}
                                {r.undone_at && <span className="ml-2 text-rose-300 text-[10px]">· UNDONE</span>}
                              </div>
                            ) : (
                              <span>
                                {r.changed_fields?.length
                                  ? `${r.changed_fields.length} field${r.changed_fields.length > 1 ? "s" : ""} changed`
                                  : r.note || ""}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            {r.entity_type === "import" && !r.undone_at && (r.created_ids?.length || 0) > 0 && (
                              <button
                                onClick={() => doUndo(r)}
                                className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                                data-testid={`audit-undo-${r.id}`}
                              >
                                <Undo2 className="h-3 w-3" /> Undo import
                              </button>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr key={`${r.id}-expand`} className="border-b border-[#1a1a1a]">
                            <td colSpan={7} className="px-8 py-4 bg-[#0d0d0d]">
                              <AuditRowDetail row={r} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )
      ))}

      {rows.length === 0 && !loading && (
        <div className="card-surface p-10 text-center text-sm text-zinc-500" data-testid="audit-empty">
          No audit events for the current filters.
        </div>
      )}
    </div>
  );
}

function AuditRowDetail({ row }) {
  const fields = row.changed_fields || [];
  if (row.entity_type === "import") {
    return (
      <div className="text-xs text-zinc-300 space-y-2">
        <div><span className="text-zinc-500">File:</span> <span className="font-mono">{row.filename}</span></div>
        <div><span className="text-zinc-500">Counts:</span> {JSON.stringify(row.import_counts || {})}</div>
        <div><span className="text-zinc-500">Rows created (undoable):</span> {row.created_ids?.length || 0}</div>
        {row.undone_at && (
          <div className="text-rose-300"><span className="text-zinc-500">Undone at:</span> {formatTime(row.undone_at)} by {row.undone_by_name}</div>
        )}
        {row.note && <div className="text-zinc-500">Note: {row.note}</div>}
      </div>
    );
  }
  if (fields.length === 0) {
    return <div className="text-xs text-zinc-500">No field-level changes recorded.{row.note ? ` Note: ${row.note}` : ""}</div>;
  }
  return (
    <table className="w-full text-xs" data-testid="audit-row-detail">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
          <th className="pb-2 font-medium w-40">Field</th>
          <th className="pb-2 font-medium">Before</th>
          <th className="pb-2 font-medium">After</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((f) => (
          <tr key={f.field} className="border-b border-[#1a1a1a] last:border-0">
            <td className="py-1.5 text-zinc-400 font-mono">{f.field}</td>
            <td className="py-1.5 text-rose-300/90 font-mono">{formatVal(f.old)}</td>
            <td className="py-1.5 text-emerald-300/90 font-mono">{formatVal(f.new)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return <span className="text-zinc-600">—</span>;
  if (typeof v === "object") return <span className="text-zinc-500">{JSON.stringify(v).slice(0, 140)}</span>;
  const s = String(v);
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
}

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
